<script setup lang="ts">
import type { AcceptBidDto } from "@/model/championship/accept-bid-dto"
import type { ChampionshipCategory } from "@/model/championship/championship-category"
import { injectMandatory, useHttpClient } from "@/vf"
import AnimateIf from "@/vf/components/AnimateIf.vue"
import { computed, reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { TaskViewMode } from "../../Issue/util"
import {
    validateTeamName,
    type BlankRegistration,
    type ChampionshipRegistrationContactPerson,
    type ChampionshipRegistrationSelectTeamData,
    type ChampionshipRegistrationTeamMember,
} from "../championship-registration"
import ChampionshipRegistrationBlankRegistration from "./ChampionshipRegistrationBlankRegistration.vue"
import ChampionshipRegistrationContactPersonForm from "./ChampionshipRegistrationContactPersonForm.vue"
import ChampionshipRegistrationEditTeam from "./ChampionshipRegistrationEditTeam.vue"

const props = defineProps<{
    data: ChampionshipRegistrationSelectTeamData
}>()

const emit = defineEmits<{
    (e: "saved"): void
}>()

const { t } = useI18n()
const http = useHttpClient()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")

type StepType = "contactPerson" | "category" | "bid" | "method" | "name" | "blank" | "selectMembers"
type RegistrationMethod = "blank" | "named"

const stepNames: Record<StepType, string> = reactive({
    contactPerson: t("@tasks:tasks.championship_registration.wizard.step_button.contactPerson"),
    category: t("@tasks:tasks.championship_registration.wizard.step_button.category"),
    bid: t("@tasks:tasks.championship_registration.wizard.step_button.bid"),
    method: t("@tasks:tasks.championship_registration.wizard.step_button.method"),
    name: t("@tasks:tasks.championship_registration.wizard.step_button.name"),
    blank: t("@tasks:tasks.championship_registration.wizard.step_button.blank"),
    selectMembers: t("@tasks:tasks.championship_registration.wizard.step_button.selectMembers"),
})

const methodNames: Record<RegistrationMethod, string> = reactive({
    blank: t("@tasks:tasks.championship_registration.wizard.registration_method.blank"),
    named: t("@tasks:tasks.championship_registration.wizard.registration_method.named"),
    teamFilter: t("@tasks:tasks.championship_registration.wizard.registration_method.teamFilter"),
})

/*
step order:
category -> contactPerson -> name -> method
*/

/*─────────────────────────────────────┐
│  wizard steps                        │
└─────────────────────────────────────*/
const currentStep = ref<StepType>("category")
function nextStep() {
    switch (currentStep.value) {
        case "category":
            resetBids()
            if (bidsRequiringAcceptance.value.length > 0) {
                currentStep.value = "bid"
            } else {
                currentStep.value = "contactPerson"
            }
            previousSteps.push({ type: "category" })
            break
        case "bid":
            currentStep.value = "contactPerson"
            previousSteps.push({ type: "bid" })
            break
        case "contactPerson":
            currentStep.value = "name"
            previousSteps.push({ type: "contactPerson" })
            break
        case "name":
            currentStep.value = "method"
            previousSteps.push({ type: "name" })
            break
        case "method":
            currentStep.value = (
                {
                    blank: "blank",
                    named: "selectMembers",
                } as Record<RegistrationMethod, StepType>
            )[registrationMethod.value!]
            previousSteps.push({ type: "method" })
            break
    }
}

function backToStep(step: StepType) {
    currentStep.value = step

    // remove all "previous steps" from the list that are after the selected step
    const index = previousSteps.findIndex(x => x.type === step)
    if (index >= 0) {
        previousSteps.splice(index)
    }
}

const previousSteps = reactive<{ type: StepType }[]>([])

/*─────────────────────────────────────┐
│  contact person                      │
└─────────────────────────────────────*/
const safetyChampion = ref<ChampionshipRegistrationContactPerson | undefined>(undefined)
const accuscoreMember = ref<ChampionshipRegistrationContactPerson | undefined>(undefined)

/*─────────────────────────────────────┐
│  bid                                 │
└─────────────────────────────────────*/
const bidAcceptances = ref<AcceptBidDto[]>([])

const bidsRequiringAcceptance = computed(() =>
    (selectedCategory.value?.bids ?? []).filter(bid => bid.acceptanceNeeded !== false),
)

function resetBids() {
    bidAcceptances.value = bidsRequiringAcceptance.value.map(bid => ({ id: bid.id, name: bid.name }))
}

function allBidsAnswered(): boolean {
    return bidAcceptances.value.length > 0 && bidAcceptances.value.every(bid => bid.accepted !== undefined)
}

/*─────────────────────────────────────┐
│  registration method                 │
└─────────────────────────────────────*/
const registrationMethod = ref<RegistrationMethod>()

function selectMethod(method: RegistrationMethod) {
    registrationMethod.value = method
    nextStep()
}

/*─────────────────────────────────────┐
│  category selection                  │
└─────────────────────────────────────*/
const selectedCategory = ref<ChampionshipCategory>()
const categoryValid = computed(() => !!selectedCategory.value)
const categoryHasAccuscore = computed(() => selectedCategory.value?.isEnableAccuscore ?? false)

/*─────────────────────────────────────┐
│  name                                │
└─────────────────────────────────────*/
const teamName = ref<string>()
const teamNameValid = computed(() => validateTeamName(teamName.value))

/*─────────────────────────────────────┐
│  select members                      │
└─────────────────────────────────────*/
const members = ref<ChampionshipRegistrationTeamMember[]>([])

/*─────────────────────────────────────┐
│  blank                               │
└─────────────────────────────────────*/
const blankRegistration = reactive<BlankRegistration>({
    numberOfParticipants: undefined,
    numberOfAlternates: undefined,
    numberOfEntourages: undefined,
})

/*─────────────────────────────────────┐
│  submit                              │
└─────────────────────────────────────*/
// when set to true, the submit buttons are disabled
const submitting = ref(false)

async function save() {
    if (registrationMethod.value === "blank") {
        await registerBlank()
    } else {
        await registerNamed()
    }

    emit("saved")
}

async function registerBlank() {
    submitting.value = true
    try {
        await http.post(
            `/championship/registration/${props.data.registrationId}/select-team/register-blank?viewMode=${viewMode}`,
            {
                teamName: teamName.value,
                categoryId: selectedCategory.value!.id,
                safetyChampionMemberId: safetyChampion.value?.id,
                accuscoreMemberId: accuscoreMember.value?.id,
                bidAcceptances: bidAcceptances.value,
                ...blankRegistration,
            },
        )
    } finally {
        submitting.value = false
    }
}

async function registerNamed() {
    submitting.value = true
    try {
        await http.post(
            `/championship/registration/${props.data.registrationId}/select-team/register-named?viewMode=${viewMode}`,
            {
                teamName: teamName.value,
                categoryId: selectedCategory.value!.id,
                members: members.value,
                safetyChampionMemberId: safetyChampion.value?.id,
                accuscoreMemberId: accuscoreMember.value?.id,
                bidAcceptances: bidAcceptances.value,
            },
        )
    } finally {
        submitting.value = false
    }
}
</script>
<template>
    <div class="pl-3 mb-5 mt-4">
        <button
            v-for="step of previousSteps"
            :key="step.type"
            class="d-flex flex-row align-items-center py-3 px-4 bg-primary text-white mb-3 wizard-button font-weight-normal"
            @click="backToStep(step.type)"
        >
            <div>
                <i class="fa fa-fw fa-chevron-left"></i>
            </div>
            <div class="flex-grow-1 pl-3">
                <b>{{ stepNames[step.type] }}</b>

                <div v-if="step.type === 'contactPerson' && safetyChampion">
                    {{ safetyChampion.name }}, {{ safetyChampion.email }}
                </div>

                <div v-if="step.type === 'category'">
                    {{ selectedCategory?.name }}
                </div>

                <div v-if="step.type === 'bid'">
                    {{
                        bidAcceptances
                            .map(
                                b =>
                                    `${b.name}: ${
                                        b.accepted
                                            ? $t("@tasks:tasks.championship_registration.wizard.bid.yes")
                                            : $t("@tasks:tasks.championship_registration.wizard.bid.no")
                                    }`,
                            )
                            .join(", ")
                    }}
                </div>

                <div v-if="step.type === 'method'">
                    {{ methodNames[registrationMethod!] }}
                </div>

                <div v-if="step.type === 'name'">
                    {{ teamName }}
                </div>
            </div>
        </button>

        <AnimateIf :if="currentStep === 'contactPerson'">
            <form @submit.prevent="nextStep()" class="px-2">
                <ChampionshipRegistrationContactPersonForm
                    :data="data"
                    v-model="safetyChampion"
                    :label="$t('@championship:registration.window.edit_team.contactPerson.select_safety_champion')"
                />

                <ChampionshipRegistrationContactPersonForm
                    :data="data"
                    v-model="accuscoreMember"
                    :label="$t('@championship:registration.window.edit_team.contactPerson.select_accuscore_member')"
                    v-if="categoryHasAccuscore && data?.availableContactPersons?.length"
                />

                <div
                    class="alert alert-warning d-flex align-items-center mt-4 mb-0"
                    v-if="data?.availableContactPersons?.length"
                >
                    <i class="fas fa-triangle-exclamation fa-lg mr-3"></i>
                    <b>{{ $t("@tasks:tasks.championship_registration.contact_person_must_be_registered_hint") }}</b>
                </div>

                <button class="btn btn-primary mt-4" type="submit">
                    {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                </button>
            </form>
        </AnimateIf>

        <AnimateIf :if="currentStep === 'category'">
            <form @submit.prevent="nextStep()" class="px-2">
                <div>
                    <b>
                        {{ $t("@tasks:tasks.championship_registration.wizard.please_provide_category") }}
                    </b>
                </div>

                <label for="categoryId" class="mt-3">
                    {{ $t("@tasks:tasks.championship_registration.wizard.category") }}
                </label>
                <select id="categoryId" class="form-control" v-model="selectedCategory">
                    <option v-for="category of data.categories" :value="category" :key="category.id">
                        {{ category.name }}
                    </option>
                </select>

                <div v-if="categoryValid" class="mt-3">
                    <div>
                        {{
                            $t("@tasks:tasks.championship_registration.wizard.selected_category_info.birth_years", {
                                min: selectedCategory!.minBirthYear,
                                max: selectedCategory!.maxBirthYear,
                            })
                        }}
                    </div>
                    <div>
                        {{
                            $t(
                                "@tasks:tasks.championship_registration.wizard.selected_category_info.participant_count",
                                {
                                    min: selectedCategory!.criteriaMinStarters,
                                    max: selectedCategory!.criteriaMaxStarters,
                                },
                            )
                        }}
                    </div>
                    <div v-if="selectedCategory!.criteriaMaxMale ?? 0 > 0">
                        {{
                            $t(
                                "@tasks:tasks.championship_registration.wizard.selected_category_info.male_participants",
                                {
                                    min: selectedCategory!.criteriaMinMale,
                                    max: selectedCategory!.criteriaMaxMale,
                                },
                            )
                        }}
                    </div>
                    <div v-else>
                        {{
                            $t(
                                "@tasks:tasks.championship_registration.wizard.selected_category_info.no_male_participants",
                                {
                                    min: selectedCategory!.criteriaMinMale,
                                    max: selectedCategory!.criteriaMaxMale,
                                },
                            )
                        }}
                    </div>
                </div>

                <button class="btn btn-primary mt-4" type="submit" :disabled="!categoryValid">
                    {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                </button>
            </form>
        </AnimateIf>
        <AnimateIf :if="currentStep === 'bid'">
            <form @submit.prevent="nextStep()" class="px-2 pl-4">
                <div v-for="bid in bidAcceptances" :key="bid.id" class="mb-4">
                    <div class="mb-2">
                        <b>{{ bid.name }}</b>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`wizard-accept-${bid.id}`"
                            :name="`wizard-bid-${bid.id}`"
                            :value="true"
                            v-model="bid.accepted"
                        />
                        <label class="form-check-label" :for="`wizard-accept-${bid.id}`">
                            {{ $t("@tasks:tasks.championship_registration.wizard.bid.accept", { bid: bid.name }) }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`wizard-decline-${bid.id}`"
                            :name="`wizard-bid-${bid.id}`"
                            :value="false"
                            v-model="bid.accepted"
                        />
                        <label class="form-check-label" :for="`wizard-decline-${bid.id}`">
                            {{ $t("@tasks:tasks.championship_registration.wizard.bid.decline", { bid: bid.name }) }}
                        </label>
                    </div>
                </div>
                <button class="btn btn-primary mt-3" type="submit" :disabled="!allBidsAnswered()">
                    {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                </button>
            </form>
        </AnimateIf>
        <AnimateIf :if="currentStep === 'method'">
            <div class="pl-5">
                <div class="mb-3">
                    <b>
                        {{ $t("@tasks:tasks.championship_registration.wizard.please_select_method") }}
                    </b>
                </div>
                <button class="wizard-button" @click="selectMethod('blank')">
                    {{ $t("@tasks:tasks.championship_registration.wizard.register_blank") }}
                </button>
                <button class="wizard-button" @click="selectMethod('named')">
                    {{ $t("@tasks:tasks.championship_registration.wizard.register_named") }}
                </button>
            </div>
        </AnimateIf>
        <AnimateIf :if="currentStep === 'name'">
            <form @submit.prevent="nextStep()" class="px-2 pl-4">
                <div class="mb-3">
                    <b>
                        {{ $t("@tasks:tasks.championship_registration.wizard.please_enter_name") }}
                    </b>
                </div>
                <div class="my-3">
                    <b>{{ $t("@tasks:tasks.championship_registration.wizard.team_name") }}</b>
                    <input type="text" class="form-control" v-model="teamName" />
                </div>

                <button class="btn btn-primary mt-4" type="submit" :disabled="!teamNameValid">
                    {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                </button>
            </form>
        </AnimateIf>
        <AnimateIf :if="currentStep === 'blank'">
            <div class="pl-4">
                <ChampionshipRegistrationBlankRegistration
                    :category="selectedCategory!"
                    v-model="blankRegistration"
                    :submitting="submitting"
                    @save="save()"
                />
            </div>
        </AnimateIf>
        <AnimateIf :if="currentStep === 'selectMembers'">
            <ChampionshipRegistrationEditTeam
                v-bind="{ categoryId: selectedCategory!.id! }"
                v-model="members"
                :submitting="submitting"
                @save="save()"
            />
        </AnimateIf>
    </div>
</template>
